import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles } from "lucide-react";

/** Subtle banner shown at the top of Dashboard when user is on trial
 *  (especially in the last 7 days) or already expired. */
export default function TrialBanner() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/billing/me")
      .then(({ data }) => mounted && setState(data))
      .catch(() => mounted && setState(null));
    return () => {
      mounted = false;
    };
  }, []);

  if (!state) return null;

  // Active paid user → no banner.
  if (state.status === "active" && !state.in_trial) return null;

  // Expired → strong banner
  if (state.status === "expired") {
    return (
      <div
        data-testid="trial-banner-expired"
        className="rounded-2xl bg-[#2D2926] text-[#FBF9F6] px-5 py-4 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <Sparkles size={18} strokeWidth={1.5} className="text-[#D4A574]" />
          <span className="text-sm">
            Your trial ended. Your conversations are still here — get Colleague+ to keep going.
          </span>
        </div>
        <Link
          to="/pricing"
          data-testid="trial-banner-cta"
          className="bg-[#D4A574] text-[#2D2926] hover:bg-[#C99560] rounded-full px-4 py-2 text-sm font-medium transition-colors text-center"
        >
          Get Colleague+ — from $9.99
        </Link>
      </div>
    );
  }

  // Trial → only show in last 7 days
  if (state.in_trial && state.days_remaining > 7) return null;

  const days = state.days_remaining;
  return (
    <div
      data-testid="trial-banner-warning"
      className="rounded-2xl bg-[#FBF6EE] border border-[#E8D5B8] px-5 py-3 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
    >
      <div className="flex items-center gap-3 text-sm text-[#3D3833]">
        <Sparkles size={16} strokeWidth={1.5} className="text-[#D4A574]" />
        <span>
          {days === 0 ? (
            <>Your trial ends today.</>
          ) : days === 1 ? (
            <>1 day left in your free trial.</>
          ) : (
            <>{days} days left in your free trial.</>
          )}{" "}
          <span className="text-[#6B655D]">Get Colleague+ for $9.99/mo.</span>
        </span>
      </div>
      <Link
        to="/pricing"
        data-testid="trial-banner-cta"
        className="bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-4 py-1.5 text-xs uppercase tracking-wider font-medium transition-colors text-center"
      >
        See pricing
      </Link>
    </div>
  );
}
